import { useMemo } from 'react';
import { useDynamicEntity } from './useDynamicEntity';
import { useAuth } from './index';
import { useAccessibleFirmIds } from './useAccessibleFirmIds';
import { formatDateForTimestamptz } from '../utils/dateUtils';

const useOffers = ({ paginated = false } = {}) => {
  const { user } = useAuth();
  const { firmIds, loading: firmIdsLoading, isRestricted } = useAccessibleFirmIds();

  const customWhereCondition = useMemo(() => {
    if (user?.role === 'ROOT') return {};
    if (isRestricted) {
      const safeIds = firmIds && firmIds.length > 0 ? firmIds : [-1];
      return {
        company_id: { _eq: user?.companyId },
        _or: [
          { created_by: { _eq: user?.id } },
          { firm_id: { _in: safeIds } },
        ],
      };
    }
    return { company_id: { _eq: user?.companyId } };
  }, [user?.role, user?.companyId, user?.id, isRestricted, firmIds]);

  const firmIdsKey = isRestricted ? (firmIds ? firmIds.join(',') : 'loading') : 'unrestricted';
  const dynamicHook = useDynamicEntity({
    entityType: 'offers',
    gqlEntityKey: 'offers',
    queryName: 'GET_OFFERS_QUERY',
    createMutation: 'CREATE_OFFER_MUTATION',
    updateMutation: 'UPDATE_OFFER_MUTATION',
    deleteMutation: 'DELETE_OFFER_MUTATION',
    updateVariableName: 'set',
    serverPagination: paginated,
    aggregateKey: paginated ? 'offers_aggregate' : null,
    transformData: (offer) => {
      const creator = offer.created_by_user ?? offer.user;
      const createdByName = creator
        ? [creator.first_name, creator.last_name].filter(Boolean).join(' ').trim() || '-'
        : '-';
      return {
        ...offer,
        company_name: offer.company?.name || (offer.company_id ? `Company ${offer.company_id}` : '-'),
        firm_name: offer.firm?.firm_name || offer.firm_short_name || (offer.firm_id ? `Firm ${offer.firm_id}` : '-'),
        firm_short_name: offer.firm_short_name || offer.firm?.firm_short_name || '-',
        contract_subject_name: offer.contract_subject?.name || (offer.contract_subject_id ? `Subject ${offer.contract_subject_id}` : '-'),
        scope_of_work_name: offer.scope_of_work?.name || (offer.scope_of_work_id ? `Scope ${offer.scope_of_work_id}` : '-'),
        proposal_status_name: offer.proposal_status?.name || (offer.proposal_status_id ? `Status ${offer.proposal_status_id}` : 'Belirtilmemiş'),
        firm_established: offer.firm_established || false,
        proposal_given: offer.proposal_given || false,
        revised_proposal_given: offer.revised_proposal_given || false,
        revised_proposals: offer.revised_proposals || [],
        companyId: offer.company_id,
        firmId: offer.firm_id,
        created_by_name: createdByName,
        sectorIds: Array.isArray(offer.sector_id) ? offer.sector_id : [],
      };
    },
    transformFormData: (formData) => {
      let firm_id = null;
      let firm_short_name = null;
      
      if (formData.firm_established === 'yes') {
        firm_id = formData.firmId;
        firm_short_name = null;
      } else {
        // firm_short_name seçimi aslında firm_id'yi temsil ediyor
        firm_id = formData.firm_short_name; // Bu artık firm_id
        firm_short_name = null;
      }
      
      const result = {
        company_id: formData.companyId,
        firm_id: firm_id,
        firm_short_name: firm_short_name,
        firm_established: formData.firm_established === 'yes',
        proposal_given: formData.proposal_given === 'yes',
        contract_subject_id: formData.contract_subject_id,
        scope_of_work_id: formData.scope_of_work_id,
        proposal_date: formatDateForTimestamptz(formData.proposal_date),
        proposal_status_id: formData.proposal_status_id,
        proposal_file_key: formData.proposal_file === null ? null : (formData.proposal_file?.key || formData.proposal_file_key || null),
        proposal_file_size: formData.proposal_file === null ? null : (formData.proposal_file?.size || formData.proposal_file_size || null),
        revised_proposal_given: formData.revised_proposal_given === 'yes',
        revised_proposals: (formData.revised_proposals || []).map(proposal => ({
          id: proposal.id,
          date: formatDateForTimestamptz(proposal.date),
          file: proposal.file,
          comment: proposal.comment || null,
        })),
        proposal_status_id: formData.proposal_status_id,
        comment: formData.comment || null,
        reminder_date: formatDateForTimestamptz(formData.reminder_date),
        reminder_note: formData.reminder_note || null,
        sector_id: Array.isArray(formData.sectorIds) ? formData.sectorIds : [],
      };
      if (!formData.id) {
        result.created_by = user?.id ?? null;
      }
      return result;
    },
    customWhereCondition,
    customDepsKey: firmIdsKey,
  });
  return {
    ...dynamicHook,
    isLoading: dynamicHook.isLoading || (isRestricted && firmIdsLoading),
    isRootUser: user?.role === 'ROOT',
  };
};

export { useOffers }; 